import { useMutation, useQuery, useQueryClient } from "react-query";
import { OrganizationKeyName } from "../../../model/Organization";
import { cacheKeys } from "../../../service/api";
import { venueHttp } from "./venue-http";
import { UploadImage } from "../../../service/api-response/UploadImage";
import {
  CreateRestaurant,
  DeleteRestaurantImage,
  UpdateRestaurant,
} from "../../../model/Venue";
import { VenueListItemResponse } from "../../../service/api-response/VenuesSuccessResponse";
import { VenueTagsItemResponse } from "../../../service/api-response/VenueTagsSuccessResponse";

export const useGetRestaurant = (
  organizationKeyName?: OrganizationKeyName,
  id?: string
) => {
  return useQuery(
    [cacheKeys.restaurant, id],
    () => venueHttp.getRestaurant(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled: !!id }
  );
};

export const useUploadRestaurantImage = () => {
  return useMutation<string, any, UploadImage>(venueHttp.uploadRestaurantImage);
};

export const useUploadRestaurantLogo = () => {
  return useMutation<any, any, UploadImage>(venueHttp.uploadRestaurantLogo);
};

export const useCreateRestaurant = () => {
  const queryClient = useQueryClient();

  return useMutation<string, any, CreateRestaurant>(
    venueHttp.createRestaurant,
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(cacheKeys.restaurants);
      },
    }
  );
};

export const useUpdateRestaurant = (id?: string) => {
  const queryClient = useQueryClient();

  return useMutation<any, any, UpdateRestaurant>(venueHttp.updateRestaurant, {
    onSuccess: async () => {
      await queryClient.refetchQueries(cacheKeys.restaurants);
      if (id) {
        await queryClient.refetchQueries([cacheKeys.restaurant, id]);
      }
    },
  });
};

export const useDeleteRestaurantImage = (id?: string | number) => {
  const queryClient = useQueryClient();

  return useMutation<any, any, DeleteRestaurantImage>(
    venueHttp.deleteRestaurantImage,
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(cacheKeys.restaurants);
        if (id) {
          await queryClient.refetchQueries([cacheKeys.restaurant, id]);
        }
      },
    }
  );
};

export const useGetRestaurants = (organizationKeyName: OrganizationKeyName) => {
  return useQuery<VenueListItemResponse[], any>(
    [cacheKeys.restaurants],
    () => venueHttp.getRestaurants(organizationKeyName),
    { refetchOnWindowFocus: false }
  );
};

export const useGetRestaurantTags = (
  organizationKeyName: OrganizationKeyName
) => {
  return useQuery<VenueTagsItemResponse[], any>(
    [cacheKeys.restaurantTags],
    () => venueHttp.getRestaurantTags(organizationKeyName),
    { refetchOnWindowFocus: false }
  );
};
